// SendGrid-specific utility functions for scope and access-based data organization

/**
 * Group SendGrid scopes by access level (Read, Read & Write)
 * @param {Array} scopes - Array of scope objects with access property
 * @returns {Object} Grouped scopes by access level
 */
export function groupSendGridByAccess(scopes) {
  const accessGroups = {
    'Read': [],
    'Read & Write': []
  };

  scopes.forEach(scope => {
    const access = scope.access || 'Read';
    if (accessGroups[access]) {
      accessGroups[access].push(scope);
    } else {
      // Handle any unexpected access levels
      accessGroups['Read'].push(scope);
    }
  });

  return accessGroups;
}

/**
 * Group SendGrid scopes by main scope category
 * @param {Array} scopes - Array of scope objects
 * @returns {Object} Grouped scopes by main scope name
 */
export function groupSendGridByScope(scopes) {
  const scopeGroups = {};

  scopes.forEach(scope => {
    const scopeName = scope.scope || 'Unknown';
    if (!scopeGroups[scopeName]) {
      scopeGroups[scopeName] = [];
    }
    scopeGroups[scopeName].push(scope);
  });

  return scopeGroups;
}

/**
 * Group SendGrid scopes by sub-scope within main scopes
 * @param {Array} scopes - Array of scope objects
 * @returns {Object} Grouped scopes by main scope, then sub-scope
 */
export function groupSendGridBySubScope(scopes) {
  const subScopeGroups = {};

  scopes.forEach(scope => {
    const mainScope = scope.scope || 'Unknown';
    const subScope = scope.sub_scope || 'General';
    
    if (!subScopeGroups[mainScope]) {
      subScopeGroups[mainScope] = {};
    }
    
    if (!subScopeGroups[mainScope][subScope]) {
      subScopeGroups[mainScope][subScope] = [];
    }
    
    subScopeGroups[mainScope][subScope].push(scope);
  });

  return subScopeGroups;
}

/**
 * Get SendGrid access statistics
 * @param {Array} scopes - Array of scope objects
 * @returns {Object} Statistics for each access level
 */
export function getSendGridAccessStats(scopes) {
  const stats = {
    'Read': 0,
    'Read & Write': 0
  };

  scopes.forEach(scope => {
    const access = scope.access || 'Read';
    if (stats[access] !== undefined) {
      stats[access]++;
    } else {
      stats['Read']++;
    }
  });

  return stats;
}

/**
 * Get SendGrid scope statistics
 * @param {Array} scopes - Array of scope objects
 * @returns {Object} Statistics for each main scope
 */
export function getSendGridScopeStats(scopes) {
  const stats = {};

  scopes.forEach(scope => {
    const scopeName = scope.scope || 'Unknown';
    stats[scopeName] = (stats[scopeName] || 0) + 1;
  });

  return stats;
}

/**
 * Create permission list items for display
 * @param {Array} permissions - Array of permission strings
 * @returns {Array} Array of items with name property
 */
export function createSendGridPermissionItems(permissions) {
  return permissions.map(permission => ({
    name: permission,
    url: null // SendGrid permissions don't have direct URLs
  }));
}

/**
 * Get access level display labels
 * @param {string} access - The access level (Read, Read & Write)
 * @returns {string} Display label
 */
export function getAccessDisplayLabel(access) {
  const labels = {
    'Read': 'Read Only',
    'Read & Write': 'Read & Write'
  };
  
  return labels[access] || access;
}

/**
 * Get access level color scheme
 * @param {string} access - The access level (Read, Read & Write)
 * @returns {string} Color code
 */
export function getAccessColor(access) {
  const colors = {
    'Read': '#f59e0b', // amber for read-only
    'Read & Write': '#10b981' // green for read & write
  };
  
  return colors[access] || '#6b7280';
}

/**
 * Get scope category color scheme
 * @param {string} scopeName - The main scope name
 * @returns {string} Color code
 */
export function getScopeColor(scopeName) {
  const colors = {
    'API Keys': '#3b82f6', // blue
    'Alerts': '#ef4444', // red
    'Category Management': '#8b5cf6', // purple
    'IP Management': '#06b6d4', // cyan
    'Inbound Parse': '#f59e0b', // amber
    'Mail Send': '#10b981', // green
    'Mail Settings': '#f97316', // orange
    'Partners': '#84cc16', // lime
    'Security': '#dc2626', // red-600
    'Sender Authentication': '#7c3aed', // violet
    'Stats': '#0891b2', // sky-600
    'Suppressions': '#be123c', // rose-700
    'Template Engine': '#059669', // emerald-600
    'Tracking': '#0d9488', // teal-600
    'User Account': '#4338ca', // indigo-600
    'Teammates': '#be185d' // pink-700
  };
  
  return colors[scopeName] || '#6b7280';
}

/**
 * Get sub-scope display label
 * @param {string} subScope - The sub-scope name
 * @returns {string} Display label
 */
export function getSubScopeDisplayLabel(subScope) {
  if (!subScope || subScope === '') {
    return 'General';
  }
  return subScope;
}

/**
 * Count total permissions across all scopes
 * @param {Array} scopes - Array of scope objects
 * @returns {number} Total permission count
 */
export function getTotalPermissions(scopes) {
  return scopes.reduce((total, scope) => {
    return total + (scope.permissions ? scope.permissions.length : 0);
  }, 0);
}

/**
 * Get scope risk level based on access and scope type
 * @param {Object} scope - Scope object
 * @returns {string} Risk level (High, Medium, Low)
 */
export function getScopeRiskLevel(scope) {
  const highRiskScopes = ['Security', 'API Keys', 'User Account'];
  const mediumRiskScopes = ['Mail Send', 'Suppressions', 'Sender Authentication'];
  
  if (highRiskScopes.includes(scope.scope)) {
    return 'High';
  }
  
  if (mediumRiskScopes.includes(scope.scope)) {
    return 'Medium';
  }
  
  if (scope.access === 'Read & Write') {
    return 'Medium';
  }
  
  return 'Low';
}

/**
 * Get risk level color
 * @param {string} riskLevel - The risk level
 * @returns {string} Color code
 */
export function getRiskLevelColor(riskLevel) {
  const colors = {
    'High': '#dc2626', // red-600
    'Medium': '#f59e0b', // amber-500
    'Low': '#10b981' // emerald-500
  };
  
  return colors[riskLevel] || '#6b7280';
}

/**
 * Group SendGrid scopes by functional domain
 * @param {Array} scopes - Array of scope objects
 * @returns {Object} Grouped scopes by functional domain
 */
export function groupSendGridByFunctionalDomain(scopes) {
  const domainGroups = {
    'Messaging Core': [],
    'Security & Access': [],
    'Analytics': [],
    'Templates & Content': [],
    'Integrations': [],
    'User Management': []
  };

  // Define scope to domain mapping
  const scopeToDomain = {
    'Mail Send': 'Messaging Core',
    'Mail Settings': 'Messaging Core',
    'Suppressions': 'Messaging Core',
    'API Keys': 'Security & Access',
    'Security': 'Security & Access',
    'Sender Authentication': 'Security & Access',
    'Stats': 'Analytics',
    'Template Engine': 'Templates & Content',
    'Partners': 'Integrations',
    'Inbound Parse': 'Integrations',
    'Teammates': 'User Management',
    'User Account': 'User Management',
    'Alerts': 'Security & Access',
    'Category Management': 'Messaging Core',
    'IP Management': 'Security & Access',
    'Tracking': 'Analytics'
  };

  scopes.forEach(scope => {
    const domain = scopeToDomain[scope.scope] || 'Messaging Core';
    if (domainGroups[domain]) {
      domainGroups[domain].push(scope);
    }
  });

  return domainGroups;
}

/**
 * Get functional domain color
 * @param {string} domain - The functional domain name
 * @returns {string} Color code
 */
export function getFunctionalDomainColor(domain) {
  const colors = {
    'Messaging Core': '#10b981', // emerald-500
    'Security & Access': '#dc2626', // red-600
    'Analytics': '#3b82f6', // blue-500
    'Templates & Content': '#8b5cf6', // violet-500
    'Integrations': '#f59e0b', // amber-500
    'User Management': '#06b6d4' // cyan-500
  };
  
  return colors[domain] || '#6b7280';
}

/**
 * Get functional domain statistics
 * @param {Array} scopes - Array of scope objects
 * @returns {Object} Statistics for each functional domain
 */
export function getFunctionalDomainStats(scopes) {
  const domainGroups = groupSendGridByFunctionalDomain(scopes);
  const stats = {};

  Object.entries(domainGroups).forEach(([domain, scopes]) => {
    const totalPermissions = scopes.reduce((total, scope) => {
      return total + (scope.permissions ? scope.permissions.length : 0);
    }, 0);
    
    stats[domain] = {
      scopeCount: scopes.length,
      permissionCount: totalPermissions,
      subScopeCount: new Set(scopes.map(s => s.sub_scope || 'General')).size
    };
  });

  return stats;
}

/**
 * Get domain display label
 * @param {string} domain - The functional domain name
 * @returns {string} Display label
 */
export function getDomainDisplayLabel(domain) {
  const labels = {
    'Messaging Core': 'Messaging Core',
    'Security & Access': 'Security & Access',
    'Analytics': 'Analytics',
    'Templates & Content': 'Templates & Content',
    'Integrations': 'Integrations',
    'User Management': 'User Management'
  };
  
  return labels[domain] || domain;
}
